import React, { useState } from 'react';
import { X, Send, Upload, Video } from 'lucide-react';
import toast from 'react-hot-toast';

import { submitFeedback } from '../../services/api';

const FeedbackComponent = ({ onClose, onSubmit }) => {
  const [selectedType, setSelectedType] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const feedbackTypes = [
    { id: 'bug', label: 'Bug Report', icon: '🐛' },
    { id: 'feature', label: 'Feature Request', icon: '✨' },
    { id: 'improvement', label: 'Improvement', icon: '📈' },
    { id: 'other', label: 'Other Feedback', icon: '💬' },
  ];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(
      (file) =>
        file.type.startsWith('image/') || file.type.startsWith('video/'),
    );

    if (validFiles.length > 0) {
      const newAttachments = validFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        type: file.type.startsWith('image/') ? 'image' : 'video',
      }));
      setAttachments((prev) => [...prev, ...newAttachments]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video',
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!selectedType || !description.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('type', selectedType);
      formData.append('description', description);
      if (subject) formData.append('subject', subject);
      attachments.forEach((a) => formData.append('attachments', a.file));

      await submitFeedback(formData);

      setShowSuccess(true);

      setTimeout(() => {
        onSubmit?.({ type: selectedType, subject, description });
        setShowSuccess(false);
        // Reset form
        setSelectedType('');
        setSubject('');
        setDescription('');
        setAttachments([]);
        onClose?.();
      }, 2000);
    } catch (err) {
      const errorMsg = err.message || 'Something went wrong. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Success Toast */}
      {showSuccess && (
        <div className="animate-slide-down fixed left-1/2 top-4 z-[100] -translate-x-1/2 transform">
          <div className="flex items-center gap-3 rounded-lg border border-[#5652CF]/30 bg-[#1a1a1a] px-6 py-4 shadow-2xl">
            <div className="rounded-full bg-[#5652CF] p-2">
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="font-medium text-[#5652CF]">
              Thank you. Your feedback has been sent
            </p>
          </div>
        </div>
      )}

      {/* Main Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        {/* eslint-disable-next-line */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div className="relative max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-zinc-800 bg-[#1a1a1a] shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
            <h2 className="text-xl font-semibold text-white">
              Quick chat with team
            </h2>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="custom-scrollbar max-h-[calc(90vh-140px)] overflow-y-auto p-6">
            {/* Step 1: Feedback Type Selection */}
            <div className="mb-6">
              <label
                htmlFor="whats-up"
                className="mb-3 block text-sm font-medium text-zinc-300"
              >
                What's up?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {feedbackTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`rounded-xl border-2 p-4 text-left transition-all ${
                      selectedType === type.id
                        ? 'border-[#5652CF] bg-[#5652CF]/10'
                        : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{type.icon}</span>
                      <span
                        className={`font-medium ${
                          selectedType === type.id
                            ? 'text-[#5652CF]'
                            : 'text-zinc-300'
                        }`}
                      >
                        {type.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Details */}
            {selectedType && (
              <div className="animate-fade-in-fast space-y-4">
                {/* Subject (Optional) */}
                <div>
                  <label
                    htmlFor="subject"
                    className="mb-2 block text-sm font-medium text-zinc-300"
                  >
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary..."
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-900/50 px-4 py-2.5 text-white placeholder-zinc-500 transition-colors focus:border-[#5652CF] focus:outline-none"
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    htmlFor="description"
                    className="mb-2 block text-sm font-medium text-zinc-300"
                  >
                    Tell us what's on your mind...
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your feedback, bug, or suggestion in detail..."
                    rows={5}
                    className="custom-scrollbar w-full resize-none rounded-lg border border-zinc-700 bg-zinc-900/50 px-4 py-3 text-white placeholder-zinc-500 transition-colors focus:border-[#5652CF] focus:outline-none"
                  />
                </div>

                {/* File Upload Area */}
                <div>
                  <label
                    htmlFor="attachments"
                    className="mb-2 block text-sm font-medium text-zinc-300"
                  >
                    Attachments (Images/Videos)
                  </label>
                  <div
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative rounded-lg border-2 border-dashed p-6 transition-all ${
                      isDragging
                        ? 'border-[#5652CF] bg-[#5652CF]/10'
                        : 'border-zinc-700 bg-zinc-900/30 hover:border-zinc-600'
                    }`}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex cursor-pointer flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-zinc-400" />
                      <p className="text-center text-sm text-zinc-400">
                        Drag & drop files here or{' '}
                        <span className="font-medium text-[#5652CF]">
                          click to browse
                        </span>
                      </p>
                      <p className="text-xs text-zinc-500">
                        Supports images and videos
                      </p>
                    </label>
                  </div>

                  {/* Attachment Previews */}
                  {attachments.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-3">
                      {attachments.map((attachment, index) => (
                        <div key={index} className="group relative">
                          <div className="aspect-square overflow-hidden rounded-lg border border-zinc-700 bg-zinc-900">
                            {attachment.type === 'image' ? (
                              <img
                                src={attachment.preview}
                                alt={`Attachment ${index + 1}`}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <Video className="h-8 w-8 text-zinc-500" />
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-800 px-6 py-4">
            {/* Error Display */}
            {error && (
              <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="px-5 py-2.5 font-medium text-zinc-400 transition-colors hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedType || !description.trim() || isSubmitting}
                className={`flex items-center gap-2 rounded-lg px-6 py-2.5 font-medium transition-all ${
                  selectedType && description.trim() && !isSubmitting
                    ? 'bg-[#5652CF] text-white hover:bg-[#5652CF]/90'
                    : 'cursor-not-allowed bg-zinc-800 text-zinc-500'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #3f3f46;
          border-radius: 10px;
        }
      `}</style>
    </>
  );
};

export default FeedbackComponent;
